import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { KitShape } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { COUNTDOWN_DAYS, countdownDays } from '../studioRules';
import { STUDIO_SHAPES, studioMessages } from '../studioMessages';
import { kitShapeMessages } from '../../kitLabels';
import { StudioDetailsProps } from './props';
import { DetailField } from './DetailField';
import { ChoiceChip, ChoiceChips } from './ChoiceChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Step 3 for a short video with the artwork (designs n3, n3b, asset-redesign q): the shape of
 * "Artwork in motion"; the countdown has no field and lists the days it makes.
 */
const ShortDetailsComponent: React.FC<StudioDetailsProps> = ({ themeId, details, song, onChange }) => {
  const intl = useIntl();
  const shapeTitle = intl.formatMessage(studioMessages.shapeTitle);
  const shapes = useMemo<Array<ChoiceChip<KitShape>>>(
    () => STUDIO_SHAPES.map((value) => ({ value, label: intl.formatMessage(kitShapeMessages[value]) })),
    [intl]
  );

  if (themeId === 'countdown') {
    const days = countdownDays(song.daysToRelease);
    return (
      <DetailField title={intl.formatMessage(studioMessages.countdownTitle)}>
        <div className={styles.countdownDays}>
          {days.map((day) => (
            <span key={day} className={styles.countdownDay}>
              {day}
            </span>
          ))}
        </div>
        <span className="small text-muted">{intl.formatMessage(studioMessages.countdownBody)}</span>
        {days.length < COUNTDOWN_DAYS.length && (
          <span className="small text-muted">
            {intl.formatMessage(studioMessages.countdownFewer, { count: days.length })}
          </span>
        )}
      </DetailField>
    );
  }

  // Artwork in motion (owner answers 2026-10-08, page q): no moment and one length (15 s); the pill
  // follows the release date and the pre-save page.
  return (
    <DetailField title={shapeTitle} hint={intl.formatMessage(studioMessages.shapeHint)}>
      <ChoiceChips
        label={shapeTitle}
        options={shapes}
        value={details.shape}
        onChange={(shape) => onChange({ shape })}
      />
      <span className="small text-muted">{intl.formatMessage(studioMessages.shapeNote)}</span>
    </DetailField>
  );
};

export const ShortDetails = TypedMemo(ShortDetailsComponent);
